import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Papa from 'papaparse'
import filterIcon from 'BoostXpressAdmin/Assets/Icon/Stations/filter.svg'
import exportIcon from 'BoostXpressAdmin/Assets/Icon/Stations/export.svg'
import { dailyReportBXService } from 'BoostXpressAdmin/Services/dailyReportBXService'
import {
  DailyReportListItem,
  ReconciliationType,
  ReportStatus,
} from 'BoostXpressAdmin/Model/dailyReportXModel'
import { useStationOperationsList } from '../hooks/useStationOperationsList'
import {
  formatStationLitres,
  stationOperationsError,
} from 'BoostXpressAdmin/Utils/stationOperations'
import './DailyReports.scss'

const DailyReports: React.FC = () => {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [page, setPage] = useState(1)
  const limit = 10

  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [isFilterOpen, setIsFilterOpen] = useState(false)

  const {
    items: reports,
    allItems,
    total,
    isLoading,
    isError,
    error,
    refetch,
  } = useStationOperationsList<DailyReportListItem>(
    'dailyReport',
    dailyReportBXService.listAll,
    (item) =>
      `${item.station} ${item.manager}`.toLowerCase().includes(searchTerm.trim().toLowerCase()) &&
      (statusFilter === 'all' || item.status === statusFilter),
    page,
    limit,
  )
  const stats = {
    pendingReview: allItems.filter(
      (item) =>
        item.status === 'Pending Associate Review' || item.status === 'Finance Review',
    ).length,
    correctionRequired: allItems.filter(
      (item) => item.status === 'Correction Required' || item.status === 'Associate Rejected',
    ).length,
    published: allItems.filter((item) => item.status === 'Published').length,
  }
  const exportCsv = () => {
    if (!reports.length) return
    const csv = Papa.unparse(
      reports.map((r) => ({
        Station: r.station,
        Manager: r.manager,
        Variance: formatStationLitres(r.variance),
        Reconciliation: r.reconciliation,
        Status: r.status,
        'Submitted At': r.submittedAt,
      })),
    )
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `daily_reports_${new Date().toISOString().slice(0, 10)}.csv`
    document.body.appendChild(a)
    a.click()
    a.remove()
    window.URL.revokeObjectURL(url)
  }

  const renderVariance = (val: number | null) => {
    if (val === null) return <span>Not available</span>
    const isPositive = val > 0
    const isNegative = val < 0
    const colorClass = isNegative
      ? 'bx-dr-val--negative'
      : isPositive
        ? 'bx-dr-val--positive'
        : 'bx-dr-val--balanced'

    const prefix = isPositive ? '+' : ''
    return (
      <span
        className={`bx-dr-variance ${colorClass}`}
      >{`${prefix}${val.toLocaleString('en-NG', { maximumFractionDigits: 2 })} L`}</span>
    )
  }

  const renderReconciliationBadge = (type: ReconciliationType) => {
    switch (type) {
      case 'Balanced':
        return <span className='bx-dr-pill bx-dr-pill--balanced'>Balanced</span>
      case 'Overage':
        return <span className='bx-dr-pill bx-dr-pill--surplus'>Overage</span>
      case 'Shortage':
        return <span className='bx-dr-pill bx-dr-pill--deficit'>Shortage</span>
      default:
        return <span className='bx-dr-pill'>{type}</span>
    }
  }

  const renderStatusBadge = (status: ReportStatus) => {
    switch (status) {
      case 'Pending Associate Review':
        return <span className='bx-dr-status bx-dr-status--pending'>Pending Associate Review</span>
      case 'Finance Review':
        return <span className='bx-dr-status bx-dr-status--review'>Finance Review</span>
      case 'Published':
        return <span className='bx-dr-status bx-dr-status--approved'>Published</span>
      case 'Correction Required':
        return <span className='bx-dr-status bx-dr-status--returned'>Correction Required</span>
      default:
        return <span className='bx-dr-status'>{status}</span>
    }
  }

  const totalPages = Math.ceil(total / limit) || 1

  return (
    <div className='bx-daily-reports-page'>
      {isError && (
        <div className='alert alert-danger' role='alert'>
          {stationOperationsError(error)}{' '}
          <button type='button' onClick={() => refetch()}>
            Try again
          </button>
        </div>
      )}
      {/* Header section */}
      <div className='bx-daily-reports-page__header'>
        <div>
          <h2 className='bx-daily-reports-page__title'>Daily Reports</h2>
          <p className='bx-daily-reports-page__subtitle'>
            Station reports, finance review, and publication history.
          </p>
        </div>
      </div>

      {/* 3 Summary Cards */}
      <div className='row g-3 bx-daily-reports-page__stats'>
        <div className='col-12 col-md-4'>
          <div className='bx-stat-card bx-stat-card--blue'>
            <p className='bx-stat-card__title'>Pending review</p>
            <h3 className='bx-stat-card__value'>
              {isLoading ? '—' : isError ? 'Unavailable' : stats.pendingReview}
            </h3>
          </div>
        </div>
        <div className='col-12 col-md-4'>
          <div className='bx-stat-card bx-stat-card--green'>
            <p className='bx-stat-card__title'>Correction required</p>
            <h3 className='bx-stat-card__value'>
              {isLoading ? '—' : isError ? 'Unavailable' : stats.correctionRequired}
            </h3>
          </div>
        </div>
        <div className='col-12 col-md-4'>
          <div className='bx-stat-card bx-stat-card--purple'>
            <p className='bx-stat-card__title'>Published reports</p>
            <h3 className='bx-stat-card__value'>
              {isLoading ? '—' : isError ? 'Unavailable' : stats.published}
            </h3>
          </div>
        </div>
      </div>

      {/* Actions & Filters Bar */}
      <div className='bx-daily-reports-page__controls'>
        <div className='bx-daily-reports-page__search'>
          <svg
            className='bx-daily-reports-page__search-icon'
            width='18'
            height='18'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
          >
            <circle cx='11' cy='11' r='8' />
            <line x1='21' y1='21' x2='16.65' y2='16.65' />
          </svg>
          <input
            type='text'
            placeholder='Search station or station manager'
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value)
              setPage(1)
            }}
          />
        </div>

        <div className='bx-daily-reports-page__actions'>
          <div className='bx-dr-filter-dropdown-wrapper'>
            <button
              type='button'
              className={`bx-btn-outline ${isFilterOpen ? 'active' : ''}`}
              onClick={() => setIsFilterOpen(!isFilterOpen)}
            >
              <img src={filterIcon} alt='' />
              <span>Filters</span>
            </button>

            {isFilterOpen && (
              <div className='bx-dr-filter-menu'>
                <div className='bx-dr-filter-menu__title'>Filter by Status</div>
                <button
                  type='button'
                  className={`bx-dr-filter-menu__item ${statusFilter === 'all' ? 'selected' : ''}`}
                  onClick={() => {
                    setStatusFilter('all')
                    setPage(1)
                    setIsFilterOpen(false)
                  }}
                >
                  All Statuses
                </button>
                <button
                  type='button'
                  className={`bx-dr-filter-menu__item ${statusFilter === 'Associate Rejected' ? 'selected' : ''}`}
                  onClick={() => {
                    setStatusFilter('Associate Rejected')
                    setPage(1)
                    setIsFilterOpen(false)
                  }}
                >
                  Associate Rejected
                </button>
                <button
                  type='button'
                  className={`bx-dr-filter-menu__item ${statusFilter === 'Pending Associate Review' ? 'selected' : ''}`}
                  onClick={() => {
                    setStatusFilter('Pending Associate Review')
                    setPage(1)
                    setIsFilterOpen(false)
                  }}
                >
                  Pending Associate Review
                </button>
                <button
                  type='button'
                  className={`bx-dr-filter-menu__item ${statusFilter === 'Finance Review' ? 'selected' : ''}`}
                  onClick={() => {
                    setStatusFilter('Finance Review')
                    setPage(1)
                    setIsFilterOpen(false)
                  }}
                >
                  Finance Review
                </button>
                <button
                  type='button'
                  className={`bx-dr-filter-menu__item ${statusFilter === 'Published' ? 'selected' : ''}`}
                  onClick={() => {
                    setStatusFilter('Published')
                    setPage(1)
                    setIsFilterOpen(false)
                  }}
                >
                  Published
                </button>
                <button
                  type='button'
                  className={`bx-dr-filter-menu__item ${statusFilter === 'Correction Required' ? 'selected' : ''}`}
                  onClick={() => {
                    setStatusFilter('Correction Required')
                    setPage(1)
                    setIsFilterOpen(false)
                  }}
                >
                  Correction Required
                </button>
              </div>
            )}
          </div>

          <button
            type='button'
            className='bx-btn-outline'
            onClick={exportCsv}
            disabled={isLoading || !reports.length}
          >
            <img src={exportIcon} alt='' />
            <span>Export</span>
          </button>
        </div>
      </div>

      {/* Reports Table */}
      <div className='bx-daily-reports-page__table-wrapper'>
        <table className='bx-daily-reports-table'>
          <thead>
            <tr>
              <th>Station</th>
              <th>Station Manager</th>
              <th>Variance</th>
              <th>Reconciliation</th>
              <th>Status</th>
              <th>Submitted</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={6} className='bx-dr-empty'>
                  Loading reports...
                </td>
              </tr>
            ) : reports.length === 0 ? (
              <tr>
                <td colSpan={6} className='bx-dr-empty'>
                  No daily reports found.
                </td>
              </tr>
            ) : (
              reports.map((report) => (
                <tr
                  key={report.id}
                  role='link'
                  tabIndex={0}
                  onClick={() =>
                    navigate(`/boostxpress/daily-reports/${report.id}`, {
                      state: { station: report.station, manager: report.manager },
                    })
                  }
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      navigate(`/boostxpress/daily-reports/${report.id}`, {
                        state: { station: report.station, manager: report.manager },
                      })
                    }
                  }}
                  className='bx-dr-row-clickable'
                >
                  <td className='bx-dr-col-station'>{report.station}</td>
                  <td className='bx-dr-col-manager'>{report.manager}</td>
                  <td className='bx-dr-col-variance'>{renderVariance(report.variance)}</td>
                  <td className='bx-dr-col-reconciliation'>
                    {renderReconciliationBadge(report.reconciliation)}
                  </td>
                  <td className='bx-dr-col-status'>{renderStatusBadge(report.status)}</td>
                  <td className='bx-dr-col-submitted'>{report.submittedAt}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Bar */}
      <div className='bx-daily-reports-page__pagination'>
        <div className='bx-daily-reports-page__pagination-controls'>
          <button
            type='button'
            className='bx-pagination-nav'
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            ← Previous
          </button>
          <div className='bx-daily-reports-page__pagination-numbers'>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((num) => (
              <button
                key={num}
                type='button'
                className={`bx-pagination-num ${page === num ? 'active' : ''}`}
                onClick={() => setPage(num)}
              >
                {num}
              </button>
            ))}
          </div>
          <button
            type='button'
            className='bx-pagination-nav'
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  )
}

export default DailyReports
